import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Location from '@solvhealth/types/interfaces/Location';
import { Button as JigsawButton } from '@solvhealth/jigsaw';
import { OriginType } from '../../../core/util/bookingFlowRoutingTypes';
import { getSkipButtonCopy } from '../util/insuranceForm';
import { isPhotoOnlyInsuranceFlow } from '../../../core/util/location';
import Button, { ButtonVariations } from '~/components/SolvPatternLibrary/Button';

/**
 * The Material-UI SolvPatternLibrary Button is used for the ButtonVariations.link setting, where it's a
 * plain button (no background color and no outline) wth blue text that looks like a link. The
 * jigsaw library does support a button that behaves like this (variant="text") but doesn't handle the blue
 * color case so for now there's two buttons here
 */
const StyledButton = styled(Button)`
  margin-top: 15px;
  margin-left: auto;
  margin-right: auto;
  cursor: pointer;
  text-transform: none;
`;

/**
 * Jigsaw Button is used for the classic solid background button, which should be crunchberry in all cases
 * except for whitelabelled cases where it should be whatever the theme palette color is
 */
const StyledJigsawButton = styled(JigsawButton)`
  ${StyledButton}
`;

type Props = {
  handleEnterManually?: (...args: any[]) => any;
  handleSkipBack?: (...args: any[]) => any;
  handleSkipInsurance?: () => void;
  skipBack?: boolean;
  insuranceCard: any;
  location: Location;
  origin: OriginType;
  isMobileFlow: boolean;
  isInsuranceFormPage: boolean;
};

const MainCtaButton = (props: Props) => {
  const {
    skipBack,
    insuranceCard,
    handleSkipBack,
    handleSkipInsurance,
    handleEnterManually,
    location,
    origin,
    isMobileFlow,
    isInsuranceFormPage,
  } = props;

  const { t } = useTranslation(['common', 'addInsurance']);

  const { isInsuranceRequiredForBooking } = location;

  if (skipBack) {
    return null;
  }

  const continueText = t('common:continue.capitalize');

  if (isPhotoOnlyInsuranceFlow(location) && insuranceCard.front.base64) {
    return <StyledJigsawButton onClick={handleSkipInsurance}>{continueText}</StyledJigsawButton>;
  }

  if (insuranceCard.front.base64 && !insuranceCard.back.base64) {
    return <StyledJigsawButton onClick={handleSkipBack}>{continueText}</StyledJigsawButton>;
  }

  if (!insuranceCard.front.base64 && !insuranceCard.back.base64) {
    return (
      <StyledButton
        data-testid="skipUploadInsurance"
        id="enter-manually"
        onClick={isPhotoOnlyInsuranceFlow(location) ? handleSkipInsurance : handleEnterManually}
        variation={ButtonVariations.link}
      >
        {getSkipButtonCopy(
          origin,
          isInsuranceRequiredForBooking,
          isMobileFlow,
          isInsuranceFormPage,
          t
        )}
      </StyledButton>
    );
  }

  return null;
};

export default MainCtaButton;
